<template>
  <div ref="boxComponent" class="container box_component">
    <!-- Título y botón de nueva competición -->
    <div class="header-title">
      <h2>{{ $t('athletes.title') }}</h2>
    </div>

    <!-- Barra de búsqueda y filtros -->
    <div class="filter-bar">
      <input
          v-model="searchQuery"
          @input="currentPage = 1"
          type="text"
          :placeholder="$t('competitions.view.search')"
          class="search-input"
      />
      <select
          v-model="countryFilter"
          class="country-select"
          :aria-label="$t('athletes.country_filter.label')"
      >
        <option value="all">{{ $t('athletes.country_filter.all') }}</option>
        <option
            v-for="country in availableCountries"
            :key="country.code"
            :value="country.code"
        >
          {{ country.flag }} {{ country.code }}
        </option>
      </select>
      <select
          v-model="sortOrder"
          class="status-select"
          :aria-label="$t('athletes.sort_filter.label') || 'Sort athletes'"
      >
        <option value="default">{{ $t('athletes.sort_filter.default') || 'Default order' }}</option>
        <option value="competitions_desc">{{ $t('athletes.sort_filter.competitions_desc') || 'Competitions: high to low' }}</option>
        <option value="competitions_asc">{{ $t('athletes.sort_filter.competitions_asc') || 'Competitions: low to high' }}</option>
        <option value="average_round_desc">{{ $t('athletes.sort_filter.average_round_desc') || 'Average round score: high to low' }}</option>
        <option value="average_round_asc">{{ $t('athletes.sort_filter.average_round_asc') || 'Average round score: low to high' }}</option>
      </select>
    </div>
    <!-- 🔹 VISTA DE ATLETAS -->
    <template v-if="this.athletes.length > 0">
      <!-- Grid -->
      <div :class="['athlete-grid', filteredAndPaginatedAthletes.length <= 2 ? 'single' : '']">
        <AthleteListCard
            v-for="athlete in filteredAndPaginatedAthletes"
            :key="athlete.id"
            :athlete="athlete"
            @viewAthlete="handleViewAthlete"
        />
      </div>
      <div class="pagination-controls" v-if="totalPages > 1">
        <button class="pagination-btn" @click="currentPage--" :disabled="currentPage === 1">
          {{ $t('athletes.pagination.prev') }}
        </button>

        <span class="pagination-text">
          {{ $t('athletes.pagination.page') }} {{ currentPage }} {{ $t('athletes.pagination.of') }} {{ totalPages }}
        </span>

        <button class="pagination-btn" @click="currentPage++" :disabled="currentPage === totalPages">
          {{ $t('athletes.pagination.next') }}
        </button>
      </div>
    </template>

    <p v-else class="no-athletes">{{ $t("athletes.empty_athletes") }}</p>
  </div>
    <loading-modal ref="loadingModal"></loading-modal>
</template>

<script>
import LoadingModal from "@/services/LoadingModal.vue";
import {mapActions, mapState} from "vuex";
import router from "@/router/router";
import AthleteListCard from "@/components/athletes/AthleteListCard.vue";
import {resolveResponsiveGridItemsPerPage} from "@/helpers/responsiveGridPagination";

export default {
  name: "AthletesList",
  components: {LoadingModal,AthleteListCard },
  data() {
    return {
      athletes:[],
      searchQuery: "",
      isLoading: false,
      currentPage: 1,
      itemsPerPage: 12,
      resizeObserver: null,
      resizeFrame: null,
      observedContainerWidth: 0,
      countryFilter: "all",
      sortOrder: "default",
    };
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    countryFilter() {
      this.currentPage = 1;
    },
    sortOrder() {
      this.currentPage = 1;
    }
  },
  computed: {
    ...mapState('user', ['token']),
    ...mapState("competition",["appNavigation"]),
    availableCountries() {
      const seen = new Set();
      const countries = [];
      this.athletes.forEach(athlete => {
        const code = this.resolveCountryCode(athlete);
        if (!code || seen.has(code)) {
          return;
        }
        seen.add(code);
        countries.push({
          code,
          flag: this.codeToFlag(code),
        });
      });
      return countries.sort((a, b) => a.code.localeCompare(b.code));
    },
    filteredAthletes() {
      const query = this.searchQuery.toLowerCase();

      return this.athletes.filter(athlete => {
        const matchesName = (
            athlete.name?.toLowerCase().includes(query) ||
            athlete.surname?.toLowerCase().includes(query)
        );
        if (!matchesName) return false;
        return this.matchesCountryFilter(athlete);
      });
    },
    sortedAthletes() {
      if (this.sortOrder === "default") {
        return this.filteredAthletes;
      }

      return [...this.filteredAthletes].sort((left, right) => {
        if (this.sortOrder === "competitions_desc") {
          const competitionsDiff = this.resolveCompetitionCount(right) - this.resolveCompetitionCount(left);
          if (competitionsDiff !== 0) {
            return competitionsDiff;
          }
        }

        if (this.sortOrder === "competitions_asc") {
          const competitionsDiff = this.resolveCompetitionCount(left) - this.resolveCompetitionCount(right);
          if (competitionsDiff !== 0) {
            return competitionsDiff;
          }
        }

        if (this.sortOrder === "average_round_desc") {
          const averageDiff = this.compareNullableNumbers(
            this.resolveAverageRoundScore(right),
            this.resolveAverageRoundScore(left)
          );
          if (averageDiff !== 0) {
            return averageDiff;
          }
        }

        if (this.sortOrder === "average_round_asc") {
          const averageDiff = this.compareNullableNumbers(
            this.resolveAverageRoundScore(left),
            this.resolveAverageRoundScore(right)
          );
          if (averageDiff !== 0) {
            return averageDiff;
          }
        }

        const leftName = `${left?.name || ""} ${left?.surname || ""}`.trim().toLowerCase();
        const rightName = `${right?.name || ""} ${right?.surname || ""}`.trim().toLowerCase();
        return leftName.localeCompare(rightName);
      });
    },
    filteredAndPaginatedAthletes() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.sortedAthletes.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.sortedAthletes.length / this.itemsPerPage);
    }
  },
  async mounted() {
    await this.fetchAthletes();
    if (this.appNavigation.athletes?.currentPage) {
      this.currentPage = this.appNavigation.athletes?.currentPage;
      this.searchQuery = this.appNavigation.athletes?.searchQuery;
      this.clearAppNavigationZone("athletes");
    }
    this.setupResponsivePagination();
  },
  beforeUnmount() {
    this.teardownResponsivePagination();
  },
  methods: {
    ...mapActions("competition",["updateAppNavigationZone","clearAppNavigationZone"]),
    setupResponsivePagination() {
      this.$nextTick(() => {
        this.scheduleItemsPerPageUpdate();
        window.addEventListener("resize", this.scheduleItemsPerPageUpdate);
        window.addEventListener("orientationchange", this.scheduleItemsPerPageUpdate);

        if (typeof ResizeObserver === "undefined") {
          return;
        }

        const target = this.$refs.boxComponent;
        if (!target) {
          return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0];
          const nextWidth = Math.round(entry?.contentRect?.width || 0);

          if (!nextWidth || nextWidth === this.observedContainerWidth) {
            return;
          }

          this.observedContainerWidth = nextWidth;
          this.scheduleItemsPerPageUpdate();
        });
        this.resizeObserver.observe(target);
      });
    },
    teardownResponsivePagination() {
      window.removeEventListener("resize", this.scheduleItemsPerPageUpdate);
      window.removeEventListener("orientationchange", this.scheduleItemsPerPageUpdate);
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    scheduleItemsPerPageUpdate() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateItemsPerPage();
      });
    },
    updateItemsPerPage() {
      const target = this.$refs.boxComponent;
      if (!target) {
        return;
      }

      const containerWidth = Math.round(target.clientWidth || 0);
      if (!containerWidth) {
        return;
      }

      this.observedContainerWidth = containerWidth;

      const nextItemsPerPage = resolveResponsiveGridItemsPerPage(containerWidth, window.innerWidth);
      if (nextItemsPerPage !== this.itemsPerPage) {
        this.itemsPerPage = nextItemsPerPage;
      }

      const totalPages = Math.max(1, Math.ceil(this.filteredAthletes.length / this.itemsPerPage));
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
    },
    matchesCountryFilter(athlete) {
      if (this.countryFilter === "all") {
        return true;
      }
      return this.resolveCountryCode(athlete) === this.countryFilter;
    },
    resolveCountryCode(athlete) {
      const code = athlete?.stats?.country_code || athlete?.country_code || "";
      return code.trim().toUpperCase();
    },
    resolveCompetitionCount(athlete) {
      const parsed = Number(athlete?.stats?.competitions_count ?? 0);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    resolveAverageRoundScore(athlete) {
      const parsed = Number(athlete?.stats?.average_round_score_normalized ?? athlete?.stats?.average_round_score);
      return Number.isFinite(parsed) ? parsed : null;
    },
    compareNullableNumbers(left, right) {
      const leftMissing = left === null || left === undefined;
      const rightMissing = right === null || right === undefined;

      if (leftMissing && rightMissing) {
        return 0;
      }
      if (leftMissing) {
        return 1;
      }
      if (rightMissing) {
        return -1;
      }

      return left - right;
    },
    codeToFlag(code = "") {
      if (!code || code.length < 2) {
        return "";
      }
      const letters = code
          .toUpperCase()
          .replace(/[^A-Z]/g, "")
          .slice(0, 2);
      if (letters.length < 2) {
        return "";
      }
      return letters
          .split("")
          .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
          .join("");
    },
    async fetchAthletes() {
      this.showLoading();
      try {
        const response = await this.$axios.get("/athletes/list", {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response.data?.athletes && typeof response.data.athletes === 'object') {
          this.athletes = Object.values(response.data.athletes);
        } else {
          console.warn("⚠️ La respuesta no contiene atletas válidos.");
          this.athletes = [];
        }
      } catch (error) {
        console.error("❌ Error al obtener las competiciones:", error);
      } finally {
        this.hideLoading();
      }
    },
    showLoading() {
      this.isLoading = true;
      this.$nextTick(() => {
        this.$refs.loadingModal.showLoading();
      });
    },
    hideLoading() {
      this.isLoading = false;
      this.$nextTick(() => {
        this.$refs.loadingModal.hideLoading();
      });
    },
    async handleViewAthlete(athleteId) {
      this.updateAppNavigationZone({
        zone: "athletes",
        data: {
          currentPage: this.currentPage,
          searchQuery: this.searchQuery,
        }
      });
      await router.push(`/profile/${athleteId}`);
    }
  },
};
</script>

<style scoped>
.box_component {
  width: 100%;
  max-width: 100%;
  margin: 15px auto;
}
.loading-text {
  text-align: center;
  font-size: 16px;
  color: #666;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}
.search-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-width: 250px;
}
.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}
.search-input{
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.status-select {
  min-width: 150px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
}
.country-select {
  min-width: 140px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  /* Prefer emoji-capable fonts so flag glyphs render on Windows too */
  font-family: "Twemoji Mozilla", "Noto Color Emoji", "Segoe UI Emoji", "Apple Color Emoji", "Segoe UI", system-ui, sans-serif;
}
.athlete-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}
.athlete-grid.single {
  justify-content: center;
}

.athlete-grid.single .athlete-card {
  max-width: 250px;
  width: 100%;
}
.athlete-grid .athlete-card {
  max-width: 200px;
  width: 100%;
}

.no-athletes {
  text-align: center;
  font-size: 14px;
  color: #777;
}

.athletes-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.pagination-btn {
  background-color: #4c56af;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #3b4491;
}

.pagination-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination-text {
  font-size: 14px;
  color: #444;
}

@media (max-width: 576px) {
  .athlete-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 2fr));
    gap: 2px;
  }
  .athlete-grid .athlete-card {
    max-width: 150px;
  }

  .athlete-grid.single .judge-card {
    max-width: 100%;
  }
}

@media (max-width: 768px) {

}
</style>